import { Badge, Button, Callout, Card, Field, Input, ThemeSelect, useToast } from "@ml/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, authApi } from "../../lib/api";
import { useAuth, useUser } from "../../lib/auth";
import { formatDate, planLabel, ROLE_LABELS } from "../../lib/format";
import { MIN_PASSWORD } from "../../components/accounts/shared";
import { PasswordInput } from "../auth/PasswordInput";

export function ProfileTab() {
  return (
    <div className="gfp-settings-grid">
      <ProfileCard />
      <PasswordCard />
      <AppearanceCard />
    </div>
  );
}

function AppearanceCard() {
  return (
    <Card title="Appearance" subtitle="System follows your device. Your choice also applies on magicallylinked.com.">
      <ThemeSelect />
    </Card>
  );
}

function ProfileCard() {
  const user = useUser();
  const { setUser } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [unsupported, setUnsupported] = useState(false);
  useEffect(() => setName(user.name), [user.name]);

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({ name: name.trim() }),
    onSuccess: (res) => {
      setUser(res.user);
      toast.success("Profile saved");
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) setUnsupported(true);
      else toast.error(err);
    },
  });

  const dirty = name.trim() !== user.name && name.trim().length > 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (dirty) save.mutate();
  }

  return (
    <Card title="Profile" subtitle="How you show up in Magically Linked.">
      <form className="gf-stack" onSubmit={onSubmit}>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={80} />
        </Field>
        <Field label="Email" hint="Your sign-in email. Ask your account owner to change it.">
          <Input value={user.email} readOnly disabled />
        </Field>
        <div className="gfp-kv">
          <span className="gfp-kv__k">Role</span>
          <Badge size="sm">{ROLE_LABELS[user.role]}</Badge>
          <span className="gfp-kv__k">Plan</span>
          <Badge size="sm" tone="accent">
            {planLabel(user.plan)}
          </Badge>
          <span className="gfp-kv__k">Member since</span>
          <span className="gfp-kv__v">{formatDate(user.createdAt)}</span>
        </div>
        {unsupported ? <Callout tone="info">Name changes aren't switched on yet. Check back soon.</Callout> : null}
        <div className="gf-row gf-row--end">
          <Button type="submit" variant="primary" disabled={!dirty} loading={save.isPending}>
            Save profile
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      toast.success("Password changed", "Use it next time you sign in.");
      setCurrent("");
      setNext("");
      setConfirm("");
      setTouched(false);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "invalid_password") setError("Your current password isn't right.");
      else setError(err instanceof Error ? err.message : "Couldn't change your password.");
    },
  });

  const { isImpersonating } = useAuth();
  const tooShort = next.length < MIN_PASSWORD;
  const mismatch = confirm !== next;

  if (isImpersonating) {
    return (
      <Card title="Password" subtitle="Changing it signs you out everywhere else.">
        <Callout tone="info">
          You're viewing someone else's account, so their password is off limits here. Use Reset password on their row instead.
        </Callout>
      </Card>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!current || tooShort || mismatch) return;
    change.mutate();
  }

  return (
    <Card title="Password" subtitle="Changing it signs you out everywhere else.">
      <form className="gf-stack" onSubmit={onSubmit} noValidate>
        <Field label="Current password" error={touched && !current ? "Enter your current password." : undefined}>
          <PasswordInput autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" hint={`At least ${MIN_PASSWORD} characters.`} error={touched && tooShort ? `Use at least ${MIN_PASSWORD} characters.` : undefined}>
          <PasswordInput autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirm new password" error={touched && mismatch ? "Passwords don't match." : undefined}>
          <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <div className="gf-row gf-row--end">
          <Button type="submit" variant="primary" loading={change.isPending}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}
